import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../models/NameUtils';
import { baseStyleSheet } from '../../styles/styles';
import { Modal } from '../Modal';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../components-v2/Dropdown';
import { useUserDropdown } from '../../hooks/useUserDropdown';
import { SelectAnEmployeeModal } from '../SelectAnEmployee';
import { styleSheet } from './styles';

interface UserSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onUserSelected: (user: Api.IUser) => void;
	currentUser: Api.IUser;
	userSession: Api.UserSessionContext;
	title: string;
	description: string;
	initialSelectedUser?: Api.IUser;
	selectableUsers?: Api.IUser[];
	isLoadingSelectableUsers?: boolean;
}

export function UserSelectionModal({
	isOpen,
	onClose,
	onUserSelected,
	currentUser,
	userSession,
	title,
	description,
	initialSelectedUser,
	selectableUsers,
	isLoadingSelectableUsers = false,
}: UserSelectionModalProps) {
	const selectionIsRestricted = !!selectableUsers;
	const {
		selectedUser,
		showSelectEmployeeModal,
		isLoading,
		totalUserCount,
		shouldShowUsersInDropdown,
		activeEmployees,
		setSelectedUser,
		setShowSelectEmployeeModal,
		handleCurrentUserSelect,
		handleSearchEmployeeClick,
		handleEmployeeSelect,
		handleEmployeeSelectedFromModal,
	} = useUserDropdown({
		accountId: userSession.account.id,
		currentUserId: currentUser.id,
		initialSelectedUser,
		enabled: !selectionIsRestricted,
	});

	const employees = selectableUsers
		? selectableUsers.filter(user => user.id !== currentUser.id)
		: shouldShowUsersInDropdown
			? activeEmployees
			: [];
	// The account-wide list is what `handleCurrentUserSelect` resolves the full user from;
	// when the selection is restricted, we don't have that list and just use the current user.
	const onCurrentUserSelect = selectionIsRestricted ? () => setSelectedUser(currentUser) : handleCurrentUserSelect;
	const isLoadingEmployees = selectionIsRestricted
		? isLoadingSelectableUsers
		: isLoading && !shouldShowUsersInDropdown && (totalUserCount <= 500 || totalUserCount === 0);

	const handleContinue = React.useCallback(() => {
		onUserSelected(selectedUser);
		onClose();
	}, [selectedUser, onUserSelected, onClose]);

	const handleCancel = React.useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<>
			<Modal isOpen={isOpen} onRequestClose={() => onClose()} useDefaultHeader={true}>
				<div className={css(styleSheet.modalContainer)}>
					<div className={css(styleSheet.modalTitle, baseStyleSheet.truncateText)}>{title}</div>
					<div className={css(styleSheet.modalDescription)}>{description}</div>

					<div className={css(styleSheet.content)}>
						<div className={css(styleSheet.dropdownContainer)}>
							<span className={css(styleSheet.label)} id='calendar-selection-label'>
								Select Calendar:
							</span>
							<Dropdown>
								<DropdownSelectTrigger
									styles={[styleSheet.dropdownTrigger]}
									aria-labelledby='calendar-selection-label'
									aria-expanded={false}
									role='combobox'
								>
									{getDisplayName(selectedUser)}
								</DropdownSelectTrigger>
								<DropdownContent role='listbox' aria-labelledby='calendar-selection-label'>
									{/* Always show current user first */}
									<DropdownItem
										onClick={onCurrentUserSelect}
										styles={[selectedUser.id === currentUser.id && styleSheet.selectedItem].filter(Boolean)}
										role='option'
										aria-selected={selectedUser.id === currentUser.id}
									>
										{getDisplayName(currentUser)} (You)
									</DropdownItem>

									{!selectionIsRestricted && (
										<DropdownItem onClick={handleSearchEmployeeClick} role='option' aria-label='Search for an employee'>
											Search Employee...
										</DropdownItem>
									)}

									{isLoadingEmployees && (
										<DropdownItem styles={[styleSheet.loadingItem]} role='status' aria-live='polite'>
											Loading employees...
										</DropdownItem>
									)}

									{employees.map((employee: Api.IUser) => (
										<DropdownItem
											key={employee.id}
											onClick={() => handleEmployeeSelect(employee)}
											styles={[selectedUser.id === employee.id && styleSheet.selectedItem].filter(Boolean)}
											role='option'
											aria-selected={selectedUser.id === employee.id}
											aria-label={`Select ${getDisplayName(employee)} calendar`}
										>
											{getDisplayName(employee)}
										</DropdownItem>
									))}
								</DropdownContent>
							</Dropdown>
						</div>
					</div>

					<div className={css(styleSheet.footer)}>
						<button className={css(baseStyleSheet.ctaButtonReverse, styleSheet.cancelButton)} onClick={handleCancel}>
							Cancel
						</button>
						<button className={css(baseStyleSheet.ctaButton, styleSheet.continueButton)} onClick={handleContinue}>
							Continue
						</button>
					</div>
				</div>
			</Modal>
			{showSelectEmployeeModal && (
				<SelectAnEmployeeModal
					onClose={() => setShowSelectEmployeeModal(false)}
					onUserSelected={handleEmployeeSelectedFromModal}
					bodyText='Search for an employee whose calendar you want to use for this appointment.'
				/>
			)}
		</>
	);
}
